import { useEffect, useState } from 'react';
import { createInvitationInput, ROLE_LABELS, type MemberRole } from '@buildbee/contracts';
import { ROLE_DESCRIPTIONS } from '@buildbee/domain';
import { Alert, Button, Dialog, DialogContent, Form, FormActions, FormError, FormField, Input, Select, Textarea, useToast, useZodForm } from '@buildbee/ui';
import { errorMessage, trpc } from '../../../lib/trpc';

/** Role options with the plain description shown under the picker. */
export function roleOptionsFor(roles: readonly MemberRole[]) {
  return roles.map((r) => ({ value: r, label: ROLE_LABELS[r] }));
}

type InvitableRole = Exclude<MemberRole, 'owner'>;

export function InviteDialog({
  open,
  onOpenChange,
  roles,
  defaultRole,
  onDone,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Roles the viewer may give (owners are never invited, they are promoted). */
  roles: readonly MemberRole[];
  defaultRole?: MemberRole;
  onDone: () => Promise<unknown>;
}) {
  const toast = useToast();
  const [link, setLink] = useState<string | null>(null);
  const invitable = roles.filter((r): r is InvitableRole => r !== 'owner');
  const initialRole: InvitableRole =
    defaultRole && defaultRole !== 'owner' && invitable.includes(defaultRole) ? defaultRole : invitable.includes('field') ? 'field' : (invitable[0] ?? 'field');
  const form = useZodForm(createInvitationInput, { defaultValues: { email: '', role: initialRole, message: '' } });
  useEffect(() => {
    if (open) form.setValue('role', initialRole);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialRole]);
  const role = form.watch('role') as MemberRole | undefined;
  const create = trpc.workspace.invitations.create.useMutation({
    onSuccess: async (res) => {
      toast.success('Invitation sent');
      setLink(res.inviteUrl);
      form.reset({ email: '', role: initialRole, message: '' });
      await onDone();
      if (!res.inviteUrl) onOpenChange(false);
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setLink(null);
      }}
    >
      <DialogContent title="Invite someone to your team" description="They get an email with a link to join. Clients are invited from Client portal.">
        <Form form={form} onSubmit={(v) => create.mutateAsync(v)}>
          <FormField name="email" label="Email" required>
            <Input type="email" autoFocus />
          </FormField>
          <FormField name="role" label="Role" required description={role ? ROLE_DESCRIPTIONS[role] : undefined}>
            <Select options={roleOptionsFor(invitable)} />
          </FormField>
          {role === 'field' || role === 'sub' ? (
            <p className="-mt-2 text-xs text-muted-foreground">After they join, add them to their projects from the project's Team page.</p>
          ) : null}
          <FormField name="message" label="Personal note">
            <Textarea rows={2} />
          </FormField>
          <FormError message={create.error ? errorMessage(create.error) : null} />
          {link ? (
            <Alert variant="info" title="Invite link (shown outside production only)">
              <code className="figures break-all text-xs">{link}</code>
            </Alert>
          ) : null}
          <FormActions>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            <Button type="submit" loading={create.isPending}>
              Send invitation
            </Button>
          </FormActions>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
